import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { useProjectStore } from '../store/projectStore';
import { timeEntriesAPI, categoriesAPI, Category, TimeEntryCreate, TimeEntryCategory } from '../services/api';

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedEntry {
  date: string;
  project_name: string;
  category: string;
  description: string;
  hours: number;
  error?: string;
}

export const CsvImportModal: React.FC<CsvImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { projects } = useProjectStore();

  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [parsedEntries, setParsedEntries] = useState<ParsedEntry[]>([]);

  // Fetch categories for all active projects when modal opens
  useEffect(() => {
    if (isOpen) {
      const activeProjects = projects.filter(p => p.is_active);
      Promise.all(activeProjects.map(p => categoriesAPI.list(p.id)))
        .then(results => setAllCategories(results.flat()))
        .catch(() => setAllCategories([]));
    }
  }, [isOpen, projects]);
  const [fileName, setFileName] = useState<string>('');
  const [parseError, setParseError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ created: number; errors: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setParsedEntries([]);
    setFileName('');
    setParseError('');
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError('');
    setResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          setParseError('CSV must have a header row and at least one data row');
          return;
        }

        // Parse header
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredColumns = ['date', 'project_name', 'category', 'description', 'hours'];
        const missingColumns = requiredColumns.filter(col => !header.includes(col));

        if (missingColumns.length > 0) {
          setParseError(`Missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        const dateIndex = header.indexOf('date');
        const projectIndex = header.indexOf('project_name');
        const categoryIndex = header.indexOf('category');
        const descriptionIndex = header.indexOf('description');
        const hoursIndex = header.indexOf('hours');

        // Parse data rows
        const entries: ParsedEntry[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length < header.length) continue;

          const hours = parseFloat(values[hoursIndex]);
          const entry: ParsedEntry = {
            date: values[dateIndex],
            project_name: values[projectIndex],
            category: values[categoryIndex],
            description: values[descriptionIndex],
            hours: isNaN(hours) ? 0 : hours,
          };

          // Validate
          if (!entry.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            entry.error = 'Invalid date format (use YYYY-MM-DD)';
          } else if (entry.hours <= 0 || entry.hours > 24) {
            entry.error = 'Hours must be between 0.01 and 24';
          } else if (!entry.project_name) {
            entry.error = 'Project name is required';
          } else if (!entry.description) {
            entry.error = 'Description is required';
          } else {
            // Check if project exists
            const project = projects.find(
              p => p.name.toLowerCase() === entry.project_name.toLowerCase()
            );
            if (!project) {
              entry.error = `Project "${entry.project_name}" not found`;
            }
          }

          entries.push(entry);
        }

        setParsedEntries(entries);
      } catch (err) {
        setParseError('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  const handleImport = async () => {
    const validEntries = parsedEntries.filter(e => !e.error);
    if (validEntries.length === 0) {
      setParseError('No valid entries to import');
      return;
    }

    setIsSubmitting(true);
    setParseError('');

    try {
      // Convert to TimeEntryCreate format
      const entries: TimeEntryCreate[] = validEntries.map(entry => {
        const project = projects.find(
          p => p.name.toLowerCase() === entry.project_name.toLowerCase()
        );
        const category = allCategories.find(
          c => c.name.toLowerCase() === entry.category.toLowerCase()
        );

        return {
          project_id: project!.id,
          date: entry.date,
          description: entry.description,
          hours: entry.hours,
          category: (category?.name || entry.category || 'Development') as TimeEntryCategory,
        };
      });

      const response = await timeEntriesAPI.createBulk(entries);

      setResult({
        created: response.total_created,
        errors: response.total_errors,
      });

      if (response.total_created > 0 && response.total_errors === 0) {
        setTimeout(() => {
          onSuccess();
          onClose();
          resetState();
        }, 1500);
      }

      if (response.total_errors > 0) {
        setParseError(
          `Some entries failed: ${response.errors.map(e => `Row ${e.index + 1}: ${e.error}`).join(', ')}`
        );
      }
    } catch (err: any) {
      setParseError(err.response?.data?.detail || 'Failed to import entries');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validCount = parsedEntries.filter(e => !e.error).length;
  const errorCount = parsedEntries.filter(e => e.error).length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import from CSV" size="lg">
      <div className="space-y-4">
        {/* Instructions */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            CSV Format
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            Required columns: date, project_name, category, description, hours
          </p>
          <code className="block text-xs bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300 p-2 rounded overflow-x-auto">
            date,project_name,category,description,hours<br />
            2026-01-27,Website Redesign,Development,Homepage implementation,4.0<br />
            2026-01-27,Website Redesign,Meetings,Client review,1.5
          </code>
        </div>

        {/* File Input */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-green-500 dark:hover:border-green-500 transition-colors"
          >
            <ArrowUpTrayIcon className="h-6 w-6 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              {fileName || 'Click to select CSV file'}
            </span>
          </button>
        </div>

        {/* Parsed Preview */}
        {parsedEntries.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                Preview ({validCount} valid, {errorCount} errors)
              </h4>
              <button
                onClick={resetState}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear
              </button>
            </div>
            <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Project</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Hours</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {parsedEntries.map((entry, i) => (
                    <tr
                      key={i}
                      className={entry.error ? 'bg-red-50 dark:bg-red-900/20' : ''}
                    >
                      <td className="px-3 py-2 text-gray-900 dark:text-white">{entry.date}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white truncate max-w-[150px]" title={entry.project_name}>
                        {entry.project_name}
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white">{entry.category}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white">{entry.hours}</td>
                      <td className="px-3 py-2">
                        {entry.error ? (
                          <span className="text-red-600 dark:text-red-400 text-xs">{entry.error}</span>
                        ) : (
                          <span className="text-green-600 dark:text-green-400 text-xs">Valid</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {parseError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{parseError}</p>
          </div>
        )}

        {result && result.created > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-600 dark:text-green-400">
              Successfully imported {result.created} entr{result.created !== 1 ? 'ies' : 'y'}!
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            isLoading={isSubmitting}
            disabled={validCount === 0}
          >
            Import {validCount > 0 ? `${validCount} Entr${validCount !== 1 ? 'ies' : 'y'}` : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
